import { FC, useEffect, useState } from 'react'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import SelectCourseCard from './components/SelectCourseCard/SelectCourseCard'
import { useGetSettings } from '@pages/CoursesCatalog/tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import { getDiscount } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/utils/tariff'
import FormItem from 'antd/es/form/FormItem'
import { ICustomCourse } from '../../../SetCourseClassDirectionModal/SetCourseModalBody'
import { parseAndValidateAddedCourses } from './utils/parseAddedCourses'
import sclasses from '../../../SetCourseClassDirectionModal/index.module.css'
import classes from './index.module.css'
import cn from 'classnames'
import { ICourseEnrollmentItem } from './components/SelectCourseCard/types'
import CreateLinkSection from '../../CreateLinkSection'

interface SelectAdditionalCoursesProps {
  userCourses?: number[]
  isFirstFree: boolean
  currentCourseId: number
  /** Тариф основного курса ссылки — пара course+tariff тоже занята. */
  currentTariffId?: number | null
  coursesState: { value?: ICustomCourse[]; loading: boolean }
  enrollDate?: string | null

  value?: string | null
  onChange?: (value: string | null) => void
}

const SelectAdditionalCourses: FC<SelectAdditionalCoursesProps> = ({
  userCourses = [],
  isFirstFree,
  currentCourseId,
  currentTariffId = null,
  coursesState: { value: courses = [], loading: isLoading },
  enrollDate,

  value: defValue,
  onChange,
}) => {
  const { getSettings } = useGetSettings()
  const discountsUnit = getSettings('extraCoursesDiscountUnit') ?? 'percent'
  let extraCoursesDiscounts: number[] = []
  try {
    extraCoursesDiscounts =
      JSON.parse(getSettings('extraCoursesDiscounts') ?? 'null') ?? []
  } catch {
    extraCoursesDiscounts = []
  }

  const [addedCourses, setAddedCourses] = useState<ICourseEnrollmentItem[]>(
    parseAndValidateAddedCourses(defValue ?? null),
  )

  const isAdditionalCoursePaid = (item: ICourseEnrollmentItem) => {
    if (!item?.courseId || !item?.tariffId) return false
    const tariff = courses
      .find((course) => course.id === item.courseId)
      ?.directionTariffs?.find((itemTariff) => itemTariff.id === item.tariffId)
    return !!tariff && tariff.type !== 'free'
  }

  /** Скидка только для платных доп. курсов: бесплатные в нумерации пропускаются. */
  const getPrefillDiscount = (
    index: number,
    list: ICourseEnrollmentItem[] = addedCourses,
  ): number | null => {
    let paidBefore = 0
    for (let i = 0; i < index; i++) {
      if (isAdditionalCoursePaid(list[i])) paidBefore++
    }

    const raw = Array.isArray(extraCoursesDiscounts)
      ? extraCoursesDiscounts[paidBefore]
      : null
    const value = Math.trunc(Number(raw))
    if (!Number.isFinite(value) || value <= 0) return null
    return value
  }

  useEffect(() => {
    setAddedCourses((prev) =>
      prev?.filter((course) => !userCourses?.includes(course?.courseId!)),
    )
  }, [userCourses?.length])

  useEffect(() => {
    onChange?.(JSON.stringify(addedCourses))
  }, [addedCourses, onChange])

  const onClickAdd = () => {
    setAddedCourses((prev) => [
      ...prev,
      {
        courseId: null,
        tariffId: null,
        enrollDate: null,
        value: null,
        is_period_fixed: false,
        period_unit: null,
        price: null,
        // Предзаполнение как для следующего платного; если выберут free — сбросится.
        discount: getPrefillDiscount(prev.length, prev),
        apply_discount_each_payment: false,
      },
    ])
  }

  const onRemove = (index: number) => {
    setAddedCourses((prev) => prev?.filter((_, i) => i !== index))
  }

  const onChangeItem = (
    index: number,
    key: keyof ICourseEnrollmentItem,
    value: number | string | boolean | null | object,
  ) => {
    setAddedCourses(
      (prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, [key]: value } : item,
        ) ?? [],
    )
  }

  const lastCourse = addedCourses?.[addedCourses.length - 1]

  const canAddCourse =
    !lastCourse ||
    (lastCourse?.courseId !== null && lastCourse?.tariffId !== null)

  const getAddButtonString = () => {
    const discount = getDiscount(
      isFirstFree,
      addedCourses?.length,
      addedCourses.map((item) => ({
        ...item,
        course: (courses.find((course) => course.id === item.courseId) ??
          null) as ICourse | null,
        withDemo: false,
      })),
      extraCoursesDiscounts,
      discountsUnit ?? '',
    )

    if (!discount) return 'Добавить курс'

    const nextDiscount = discount?.discount ?? 0
    const stringDiscount =
      discountsUnit === 'percent'
        ? `${nextDiscount}%`
        : `${nextDiscount} рублей`
    return "Добавить курс к покупке"//`Добавить курс со скидкой ${stringDiscount}`
  }

  /** Тарифы, уже занятые парой course+tariff в этой ссылке (основной + другие доп.). */
  const getOccupiedTariffIds = (
    index: number,
    courseId: number | null,
  ): number[] => {
    if (!courseId) return []

    const occupied = new Set<number>()

    if (currentCourseId === courseId && currentTariffId) {
      occupied.add(currentTariffId)
    }

    addedCourses.forEach((item, i) => {
      if (i === index) return
      if (item?.courseId === courseId && item?.tariffId) {
        occupied.add(item.tariffId)
      }
    })

    return Array.from(occupied)
  }

  return (
    <div className={classes.wrapper}>
      {!!addedCourses?.length && (
        <div className={classes.courseList}>
          <div className={classes.title}>Дополнительные курсы</div>
          {addedCourses?.map((order, index) => {
            const discount = getDiscount(
              isFirstFree,
              index,
              addedCourses as any,
              extraCoursesDiscounts,
              discountsUnit ?? '',
            )

            return (
              <SelectCourseCard
                key={index}
                isLoading={isLoading}
                order={order}
                allCourses={courses as ICourse[]}
                courses={courses as ICourse[]}
                occupiedTariffIds={getOccupiedTariffIds(index, order?.courseId)}
                onChange={(key, value) => onChangeItem(index, key, value)}
                onDismiss={() => onRemove(index)}
                promoCode={discount ?? undefined}
                defaultDiscount={getPrefillDiscount(index)}
                linkEnrollDate={enrollDate}
              />
            )
          })}
        </div>
      )}
      {canAddCourse && (
        <SecondaryButton
          className={cn(sclasses.tabButtonActive, classes.addCourseButton)}
          onClick={onClickAdd}
        >
          {getAddButtonString()}
        </SecondaryButton>
      )}
    </div>
  )
}

type AdditionalCoursesFieldProps = Omit<
  SelectAdditionalCoursesProps,
  'value' | 'onChange' | 'addedCourses' | 'setAddedCourses'
>

const AdditionalCoursesField: FC<AdditionalCoursesFieldProps> = (props) => {
  const { checkSettings } = useGetSettings()
  const extraCoursesAllowed = checkSettings('isAllowedExtraCoursesPurchase')

  return extraCoursesAllowed ? (
    <CreateLinkSection>
      <FormItem name="additional_courses" noStyle>
        <SelectAdditionalCourses {...props} />
      </FormItem>
    </CreateLinkSection>
  ) : null
}

export default SelectAdditionalCourses
export { AdditionalCoursesField }
